import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Monitor,
  Users,
  Clock,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Wifi,
} from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import api from '@/lib/api'

interface DashboardStats {
  totalDevices: number
  onlineDevices: number
  totalCustomers: number
  activeSessions: number
  totalSessionsToday: number
  avgSessionDuration: number
  devicesTrend: number
  sessionsTrend: number
}

interface RecentSession {
  id: string
  deviceName: string
  customerName: string
  technicianName: string
  startedAt: string
  duration: number
  status: 'active' | 'completed'
}

interface OnlineDevice {
  id: string
  name: string
  deviceId: string
  customerName: string
  status: 'online' | 'busy'
  lastSeen: string
}

export default function DashboardPage() {
  const navigate = useNavigate()

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/dashboard/stats')
      return response.data
    },
    // For demo, return mock data
    placeholderData: {
      totalDevices: 47,
      onlineDevices: 23,
      totalCustomers: 12,
      activeSessions: 3,
      totalSessionsToday: 18,
      avgSessionDuration: 1245,
      devicesTrend: 12,
      sessionsTrend: -5,
    },
  })

  const { data: recentSessions } = useQuery<RecentSession[]>({
    queryKey: ['recent-sessions'],
    queryFn: async () => {
      const response = await api.get('/sessions/recent')
      return response.data
    },
    placeholderData: [
      {
        id: '1',
        deviceName: 'Sim Rig #1',
        customerName: 'Speed Racing Center',
        technicianName: 'John Smith',
        startedAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
        duration: 900,
        status: 'active',
      },
      {
        id: '2',
        deviceName: 'Reception PC',
        customerName: 'Downtown Sim Lounge',
        technicianName: 'Sarah Johnson',
        startedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        duration: 2700,
        status: 'active',
      },
      {
        id: '3',
        deviceName: 'Sim Rig #3',
        customerName: 'ProSim Arena',
        technicianName: 'Mike Wilson',
        startedAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
        duration: 3600,
        status: 'completed',
      },
    ],
  })

  const { data: onlineDevices } = useQuery<OnlineDevice[]>({
    queryKey: ['online-devices'],
    queryFn: async () => {
      const response = await api.get('/devices/online')
      return response.data
    },
    placeholderData: [
      {
        id: '1',
        name: 'Sim Rig #1',
        deviceId: 'SC-4821-7593',
        customerName: 'Speed Racing Center',
        status: 'busy',
        lastSeen: new Date().toISOString(),
      },
      {
        id: '2',
        name: 'Reception PC',
        deviceId: 'SC-8294-1057',
        customerName: 'Downtown Sim Lounge',
        status: 'busy',
        lastSeen: new Date().toISOString(),
      },
      {
        id: '3',
        name: 'Sim Rig #2',
        deviceId: 'SC-3847-2948',
        customerName: 'Speed Racing Center',
        status: 'online',
        lastSeen: new Date().toISOString(),
      },
      {
        id: '4',
        name: 'Server Room PC',
        deviceId: 'SC-9182-4756',
        customerName: 'ProSim Arena',
        status: 'online',
        lastSeen: new Date().toISOString(),
      },
      {
        id: '5',
        name: 'Sim Rig #5',
        deviceId: 'SC-5739-8261',
        customerName: 'Downtown Sim Lounge',
        status: 'online',
        lastSeen: new Date().toISOString(),
      },
    ],
  })

  const statCards = [
    {
      title: 'Online Devices',
      value: stats?.onlineDevices || 0,
      total: stats?.totalDevices || 0,
      icon: Monitor,
      trend: stats?.devicesTrend || 0,
      color: 'text-sc-accent-green',
      bgColor: 'bg-sc-accent-green/10',
    },
    {
      title: 'Active Sessions',
      value: stats?.activeSessions || 0,
      icon: Activity,
      color: 'text-sc-primary',
      bgColor: 'bg-sc-primary/10',
    },
    {
      title: 'Total Customers',
      value: stats?.totalCustomers || 0,
      icon: Users,
      color: 'text-sc-accent-cyan',
      bgColor: 'bg-sc-accent-cyan/10',
    },
    {
      title: 'Sessions Today',
      value: stats?.totalSessionsToday || 0,
      icon: Clock,
      trend: stats?.sessionsTrend || 0,
      color: 'text-sc-accent-yellow',
      bgColor: 'bg-sc-accent-yellow/10',
    },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-sc-text-primary">Dashboard</h1>
        <p className="text-sc-text-muted mt-1">
          Overview of your remote support operations
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.title} className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sc-text-muted text-sm">{stat.title}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="stat-value">{stat.value}</span>
                  {stat.total !== undefined && (
                    <span className="text-sc-text-muted text-sm">
                      / {stat.total}
                    </span>
                  )}
                </div>
                {stat.trend !== undefined && (
                  <div
                    className={cn(
                      'flex items-center gap-1 text-xs mt-2',
                      stat.trend >= 0 ? 'text-sc-accent-green' : 'text-sc-accent-red'
                    )}
                  >
                    {stat.trend >= 0 ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3" />
                    )}
                    <span>{Math.abs(stat.trend)}% from last week</span>
                  </div>
                )}
              </div>
              <div className={cn('p-3 rounded-lg', stat.bgColor)}>
                <stat.icon className={cn('w-5 h-5', stat.color)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent sessions */}
        <div className="lg:col-span-2 card">
          <div className="card-header flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-sc-text-primary">Recent Sessions</h2>
              <p className="text-sc-text-muted text-sm mt-0.5">
                Active and completed remote sessions
              </p>
            </div>
            <Link
              to="/sessions"
              className="text-sm text-sc-primary hover:text-sc-primary-hover"
            >
              View all
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Customer</th>
                  <th>Technician</th>
                  <th>Started</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentSessions?.map((session) => (
                  <tr key={session.id}>
                    <td className="font-medium text-sc-text-primary">
                      {session.deviceName}
                    </td>
                    <td className="text-sc-text-secondary">{session.customerName}</td>
                    <td className="text-sc-text-secondary">{session.technicianName}</td>
                    <td className="text-sc-text-muted">
                      {formatRelativeTime(session.startedAt)}
                    </td>
                    <td>
                      <span
                        className={cn(
                          'badge',
                          session.status === 'active'
                            ? 'badge-busy'
                            : 'bg-sc-text-muted/10 text-sc-text-muted'
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            session.status === 'active'
                              ? 'bg-sc-accent-orange'
                              : 'bg-sc-text-muted'
                          )}
                        />
                        {session.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Online devices */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-sc-text-primary">Online Devices</h2>
              <p className="text-sc-text-muted text-sm mt-0.5">
                Ready for connection
              </p>
            </div>
            <Link
              to="/devices"
              className="text-sm text-sc-primary hover:text-sc-primary-hover"
            >
              View all
            </Link>
          </div>
          <div className="divide-y divide-sc-border-dark">
            {onlineDevices?.slice(0, 5).map((device) => (
              <div
                key={device.id}
                className="px-6 py-3 flex items-center justify-between hover:bg-sc-bg-medium/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'status-dot',
                      device.status === 'online'
                        ? 'status-dot-online'
                        : 'status-dot-busy'
                    )}
                  />
                  <div>
                    <p className="text-sm font-medium text-sc-text-primary">
                      {device.name}
                    </p>
                    <p className="text-xs text-sc-text-muted">
                      {device.customerName}
                    </p>
                  </div>
                </div>
                <button
                  className="btn-ghost text-xs py-1 px-2"
                  onClick={() => navigate('/devices')}
                >
                  Connect
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap gap-3">
            <button className="btn-primary" onClick={() => navigate('/devices')}>
              <Wifi className="w-4 h-4" />
              Start New Session
            </button>
            <button className="btn-secondary" onClick={() => navigate('/downloads')}>
              <Monitor className="w-4 h-4" />
              Add Device
            </button>
            <button className="btn-secondary" onClick={() => navigate('/customers')}>
              <Users className="w-4 h-4" />
              Invite Customer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
